import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
    Box,
    Circle,
    Icon,
    Stack,
    Flex,
    Text,
} from '@chakra-ui/react';
import { ItemModel, ArtistModel } from 'models';
import { PaginatedItemGrid, PaginatedListingGrid, Modal, ItemSearchFiltersForm, ListingSearchFiltersForm, TabGroup, ArtistGrid, InfoGuide } from 'components';
import { ItemSearchFiltersFormData } from 'components/Form/ItemSearchFiltersForm/ItemSearchFiltersForm';
import { NavigationService } from 'services';
import { BsSearch } from 'react-icons/bs';
import { IoGridOutline } from 'react-icons/io5';
import { RiAuctionLine } from 'react-icons/ri';
import { AiFillFormatPainter } from 'react-icons/ai';
import { extractFiltersFromLocation } from 'utils/search';
import { ListingSearchFiltersFormData } from 'components/Form/ListingSearchFiltersForm/ListingSearchFiltersForm';
import { getSelectedTabFromURL } from 'utils/url';
import { QuerySearchParameters } from 'constants/url';
import { useItemSearch, useListingSearch } from 'hooks/queries/useSearch';
import { useArtists } from 'hooks/queries/useArtists';
import { useMaterials } from 'hooks/queries/useMaterials';

const ExploreView: React.FC = () => {
    const location = useLocation();
    const navigate = useNavigate();

    const [modals, setModals] = useState({
        itemSearchFiltersModal: false,
        listingSearchFiltersModal: false,
    });

    const { item: defaultItemFilters, listing: defaultListingFilters } =
        extractFiltersFromLocation(location);


    const [itemSearchFilters, setItemSearchFilters] = useState<ItemSearchFiltersFormData | undefined>(defaultItemFilters);
    const [listingSearchFilters, setListingSearchFilters] = useState<ListingSearchFiltersFormData | undefined>(defaultListingFilters);
    const [itemPage, setItemPage] = useState(1);
    const [listingPage, setListingPage] = useState(1);

    const { data: itemSearchResults, isLoading: itemSearchLoading } = useItemSearch(itemSearchFilters, itemPage);
    const { data: listingSearchResults, isLoading: listingSearchLoading } = useListingSearch(listingSearchFilters, listingPage);
    const { data: artistList = [], isLoading: artistListLoading } = useArtists();
    const { data: materialList = [] } = useMaterials();

    useEffect(() => {
        const { item, listing } = extractFiltersFromLocation(location);
        setItemSearchFilters(item);
        setListingSearchFilters(listing);
    }, [location]);


    const openItemSearchModal = () => setModals({ ...modals, itemSearchFiltersModal: true });
    const closeItemSearchModal = () => setModals({ ...modals, itemSearchFiltersModal: false });
    const openListingSearchModal = () => setModals({ ...modals, listingSearchFiltersModal: true });
    const closeListingSearchModal = () => setModals({ ...modals, listingSearchFiltersModal: false });

    const handleItemSearchSubmit = (searchQuery: ItemSearchFiltersFormData) => {
        setItemSearchFilters(searchQuery);
        setItemPage(1);
        closeItemSearchModal();
    };

    const handleListingSearchSubmit = (searchQuery: ListingSearchFiltersFormData) => {
        setListingSearchFilters(searchQuery);
        setListingPage(1);
        closeListingSearchModal();
    };

    const handleListingSearchReset = () => setListingSearchFilters(undefined);

    const handleItemsPageSelect = (page: number) => {
        setItemPage(page);
        updatePageInURL(page);
    };

    const handleListingsPageSelect = (page: number) => {
        setListingPage(page);
    };

    const updateTabInURL = (tabIndex: number) => {
        const queryParams = new URLSearchParams(location.search);
        queryParams.set(QuerySearchParameters.TAB, tabIndex.toString());
        navigate(`${location.pathname}?${queryParams.toString()}`, { replace: true });
    };

    const updatePageInURL = (page: number) => {
        const queryParams = new URLSearchParams(location.search);
        queryParams.set(QuerySearchParameters.PAGE, page.toString());
        navigate(`${location.pathname}?${queryParams.toString()}`, { replace: true });
    };

    const handleTabChange = (index: number) => {
        const currentTab = getSelectedTabFromURL(location);
        if (currentTab !== index) {
            updateTabInURL(index);
        }
    };

    const handleItemClick = (item: ItemModel) =>
        navigate(NavigationService.getItemDetailsPath(item.id, item.getDisplayName()));

    const handleListingNameClick = (listingId: string, listingName: string) =>
        navigate(NavigationService.getListingDetailsPath(listingId, listingName));

    const handleListingItemClick = (itemId: string, itemName: string) =>
        navigate(NavigationService.getItemDetailsPath(itemId, itemName));

    const handleListingArtistClick = (artistId: string, artistName: string) =>
        navigate(NavigationService.getArtistDetailsPath(artistId, artistName));

    const handleArtistCardClick = (artist: ArtistModel) =>
        navigate(NavigationService.getArtistDetailsPath(artist.id, artist.name));

    const renderTabs = () => {
        const labels = [
            <Flex gap="4px" alignItems="center" direction={{ base: 'column', md: 'row' }}>
                <Icon as={AiFillFormatPainter} />
                <Text> Artists </Text>
            </Flex>,
            <Flex gap="4px" alignItems="center" direction={{ base: 'column', md: 'row' }}>
                <Icon as={IoGridOutline} />
                <Text> Items </Text>
            </Flex>,
            <Flex gap="4px" alignItems="center" direction={{ base: 'column', md: 'row' }}>
                <Icon as={RiAuctionLine} />
                <Text> Past Sales </Text>
            </Flex>,
        ];

        const content = [
            <ArtistGrid
                artists={artistList}
                isLoading={artistListLoading}
                onArtistCardClick={handleArtistCardClick}
            />,
            <Stack>
                <PaginatedItemGrid
                    onItemCardClick={handleItemClick}
                    paginatedItems={itemSearchResults}
                    onPageSelect={handleItemsPageSelect}
                    scrollMode={false}
                    gridMaxHeight={1200}
                    isLoading={itemSearchLoading}
                    showMetadata={true}
                    defaultMetadataExpanded={true}
                />
                <Circle
                    size="40px"
                    bg="gray.300"
                    left="58%"
                    transform="translateX(-50%)"
                    position="fixed"
                    bottom={100}
                    cursor="pointer"
                    onClick={openItemSearchModal}
                >
                    <Icon as={BsSearch} color="white.100" />
                </Circle>
            </Stack>,
            <Stack gap={4}>
                <InfoGuide
                    text="Note: We Only Show Publicly Available, Verifiable Auction House Sales Data."
                    dismissable
                />
                <PaginatedListingGrid
                    paginatedListings={listingSearchResults}
                    onPageSelect={handleListingsPageSelect}
                    scrollMode={false}
                    onListingNameClick={handleListingNameClick}
                    onListingItemClick={handleListingItemClick}
                    onListingArtistClick={handleListingArtistClick}
                    isLoading={listingSearchLoading}
                />
                <Circle
                    size="40px"
                    bg="gray.300"
                    left="50%"
                    transform="translateX(-50%)"
                    position="fixed"
                    bottom={100}
                    cursor="pointer"
                    onClick={openListingSearchModal}
                >
                    <Icon as={BsSearch} color="white.100" />
                </Circle>
            </Stack>,
        ];

        return (
            <TabGroup
                labels={labels}
                content={content}
                startingTab={getSelectedTabFromURL(location)}
                onChange={handleTabChange}
            />
        );
    };

    const renderModals = () => (
        <>
            <Modal
                isOpen={modals.itemSearchFiltersModal}
                onClose={closeItemSearchModal}
                showSubmitButton={false}
                showCloseButton={false}
                title="Search Items"
                content={
                    <ItemSearchFiltersForm
                        artistList={artistList}
                        materialList={materialList}
                        defaultFormData={itemSearchFilters}
                        onSubmit={handleItemSearchSubmit}
                        submitLoading={itemSearchLoading}
                    />
                }
            />
            <Modal
                isOpen={modals.listingSearchFiltersModal}
                onClose={closeListingSearchModal}
                showSubmitButton={false}
                showCloseButton={false}
                title="Search Listings"
                content={
                    <ListingSearchFiltersForm   
                        artistList={artistList}
                        defaultFormData={listingSearchFilters}
                        onSubmit={handleListingSearchSubmit}
                        onReset={handleListingSearchReset}
                        submitLoading={listingSearchLoading}
                    />
                }
            />
        </>
    );

    return (
        <Box mt="100px">
            {renderTabs()}
            {renderModals()}
        </Box>
    );
};

export default ExploreView;
